import {
  HttpParams,
  HttpRequest,
  HttpClient,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import * as angular from 'angular';
import { lastValueFrom } from 'rxjs';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { environment } from 'src/environments/environment';

interface SignatureResponse {
  signature: string;
}

@Injectable({
  providedIn: 'root',
})
export class ComponentUtilsService {
  private readonly ENDPOINT_SIGNATURE = 'signature';

  constructor(
    private httpClient: HttpClient,
    private userStateService: UserStateService,
  ) {}

  isValidUrl(value) {
    return ComponentUtilsService.validateUrl(value);
  }

  static validateUrl(value: string) {
    // Ported regular expression from url-field directive used in several widget settings (RSS, Spreadsheet, Web Page Widget, etc)
    // https://github.com/Rise-Vision/widget-settings-ui-components/blob/master/src/_angular/url-field/dtv-url-field.js#L66-L86
    const urlRegExp =
      /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
    // Add http:// if no protocol parameter exists
    if (value.indexOf('://') === -1) {
      value = 'http://' + value;
    }

    return urlRegExp.test(value);
  }

  getRequestOptions(): any {
    const authorization =
      this.userStateService.getAccessToken().token_type === 'Bearer'
        ? this.userStateService.getAccessToken().token_type +
          ' ' +
          this.userStateService.getAccessToken().access_token
        : this.userStateService.getAccessToken().access_token;

    const headers = new HttpHeaders({
      authorization: authorization,
    });

    const requestOptions = {
      headers,
      withCredentials: true,
      responseType: 'json',
    };

    return requestOptions;
  }

  async getSignature(account, provider) {
    const options = this.getRequestOptions();

    options.params = new HttpParams({
      fromObject: {
        account: account,
        provider: provider,
      },
    });

    const request = new HttpRequest(
      'GET',
      environment.DATA_SERVICE_URL + this.ENDPOINT_SIGNATURE,
      options,
    );

    try {
      const response = (await lastValueFrom(
        this.httpClient.request<SignatureResponse>(request),
      )) as HttpResponse<SignatureResponse>;

      if (response?.body?.signature) {
        return response.body.signature;
      } else {
        throw new Error('Invalid signature data');
      }
    } catch (err) {
      console.error('Failed to get signature.', err);
      throw err;
    }
  }
}

angular
  .module('risevision.template-editor.services')
  .factory('componentUtils', downgradeInjectable(ComponentUtilsService));
